import type { MessageDescriptor } from 'react-intl';

import errorMessages from '@/messages/errorMessages';

import { getCalmErrorCode, getResponseStatus } from '../utils/apiErrorShape';
import messages from './messages';

/**
 * How the screen answers a failed redemption.
 * - `already_has_access`: the user already holds an active subscription. Offer the app, not a retry.
 * - `terminal`: the backend refused the code and will keep refusing it. Explain, point at support.
 * - `retryable`: the request did not get a usable answer. Offer another attempt.
 */
export type RedemptionFailureKind = 'already_has_access' | 'terminal' | 'retryable';

export interface RedemptionFailure {
	kind: RedemptionFailureKind;
	/** The backend's wire code, when the response carried one. Logged as the failure reason. */
	code: string | null;
	status: number | null;
	heading: MessageDescriptor;
	body: MessageDescriptor;
}

// Both lists name wire codes the purchase endpoint throws; the copy for each already exists in
// errorMessages because the paid flow renders the same codes inline on its form.
// `campaign_already_redeemed_by_user` is a refusal, not access: the endpoint answers a valid
// subscription with `already_subscribed` before it touches the code, so this code means the user's
// one grant on this campaign has since expired or been revoked, and the app would unlock nothing.
const ALREADY_HAS_ACCESS_CODES = ['already_subscribed'] as const;
const REFUSED_CODES = [
	'campaign_already_redeemed_by_user',
	'campaign_code_already_redeemed',
	'campaign_code_limit_reached',
	'campaign_code_expired',
	'campaign_code_not_found',
	'campaign_not_found',
	'campaign_ended',
] as const;

type AlreadyHasAccessCode = (typeof ALREADY_HAS_ACCESS_CODES)[number];
type RefusedCode = (typeof REFUSED_CODES)[number];

const isAlreadyHasAccessCode = (code: string): code is AlreadyHasAccessCode =>
	ALREADY_HAS_ACCESS_CODES.includes(code as AlreadyHasAccessCode);
const isRefusedCode = (code: string): code is RefusedCode => REFUSED_CODES.includes(code as RefusedCode);

const RETRYABLE_CLIENT_STATUSES = [408, 429];

/**
 * Thrown when the redemption call answered 2xx without a valid subscription in the body. A
 * success status is not proof of a grant, and routing to the handoff on one would show success
 * for nothing. Classified as retryable: a grant that did land answers the retry with
 * `already_subscribed`, which the already-has-access state handles, and never with a second grant.
 */
export class UnusableGrantError extends Error {
	constructor() {
		super('redemption answered without a valid subscription');
		this.name = 'UnusableGrantError';
	}
}

export const UNUSABLE_GRANT_REASON = 'unusable_grant';

export function classifyRedemptionFailure(error: unknown): RedemptionFailure {
	if (error instanceof UnusableGrantError) {
		return {
			kind: 'retryable',
			code: UNUSABLE_GRANT_REASON,
			status: null,
			heading: messages.requestFailedHeading,
			body: messages.retryBody,
		};
	}
	const code = getCalmErrorCode(error);
	const status = getResponseStatus(error);

	if (code && isAlreadyHasAccessCode(code)) {
		return {
			kind: 'already_has_access',
			code,
			status,
			heading: messages.alreadyHaveAccessHeading,
			body: errorMessages[code],
		};
	}
	if (code && isRefusedCode(code)) {
		return {
			kind: 'terminal',
			code,
			status,
			heading: messages.cannotRedeemHeading,
			body: errorMessages[code],
		};
	}
	// Any other 4xx is a request this build cannot repair by resending it (a validation error, a wire
	// code newer than this frontend), so it gets the refused treatment with generic copy. A timeout
	// (408) or a rate limit (429) says "not now" rather than "not this request", so it joins
	// everything else (no response, a 5xx) as worth another attempt.
	if (status !== null && status >= 400 && status < 500 && !RETRYABLE_CLIENT_STATUSES.includes(status)) {
		return {
			kind: 'terminal',
			code,
			status,
			heading: messages.cannotRedeemHeading,
			body: messages.genericBody,
		};
	}
	return {
		kind: 'retryable',
		code,
		status,
		heading: messages.requestFailedHeading,
		body: messages.retryBody,
	};
}
